import { useEffect, useMemo, useState } from "react";
import { collection, addDoc, getDocs, orderBy, query, updateDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../../Firebase";
import AdminNav from "../../components/AdminNav";

function safeStr(value) {
  return String(value || "").trim();
}

function safeNum(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function fmtDate(value) {
  if (!value) return "";
  if (typeof value?.toDate === "function") return value.toDate().toLocaleDateString();
  if (typeof value === "string") {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date.toLocaleDateString();
  }
  return "";
}

export default function AdminExpenses() {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [paid, setPaid] = useState(false);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = "Expenses | Admin";
  }, []);

  async function load() {
    setLoading(true);
    setErr("");

    try {
      const col = collection(db, "expenses");
      let snap;
      try {
        const q = query(col, orderBy("updatedAt", "desc"));
        snap = await getDocs(q);
      } catch (error) {
        const q = query(col, orderBy("createdAt", "desc"));
        snap = await getDocs(q);
      }

      const list = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setRows(list);
    } catch (error) {
      setErr(error?.message || "Failed to load expenses.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const term = safeStr(search).toLowerCase();
    if (!term) return rows;

    return rows.filter((expense) => {
      return (
        safeStr(expense.name).toLowerCase().includes(term) ||
        safeStr(expense.description).toLowerCase().includes(term) ||
        safeStr(expense.id).toLowerCase().includes(term)
      );
    });
  }, [rows, search]);

  const unpaid = useMemo(() => rows.filter((expense) => !expense.paid), [rows]);
  const totalUnpaid = useMemo(
    () => unpaid.reduce((sum, expense) => sum + safeNum(expense.amount), 0),
    [unpaid]
  );

  async function handleSave(e) {
    e.preventDefault();
    if (!safeStr(name)) {
      setErr("Expense name is required.");
      return;
    }
    const parsedAmount = safeNum(amount);
    if (!parsedAmount) {
      setErr("Please enter a valid amount.");
      return;
    }

    setSaving(true);
    setErr("");

    try {
      await addDoc(collection(db, "expenses"), {
        name: safeStr(name),
        amount: parsedAmount,
        description: safeStr(description),
        paid: Boolean(paid),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setName("");
      setAmount("");
      setDescription("");
      setPaid(false);
      await load();
    } catch (error) {
      setErr(error?.message || "Failed to save expense.");
    } finally {
      setSaving(false);
    }
  }

  async function markPaid(expense) {
    if (expense.paid) return;

    try {
      const ref = doc(db, "expenses", expense.id);
      await updateDoc(ref, {
        paid: true,
        updatedAt: serverTimestamp(),
      });
      await load();
    } catch (error) {
      setErr(error?.message || "Unable to update expense status.");
    }
  }

  return (
    <div className="admin-page">
      <AdminNav />

      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", marginBottom: "1.5rem" }}>
        <div>
          <h1>Expenses</h1>
          <p>Track business expenses, record amounts, and mark paid items.</p>
        </div>
      </div>

      <div className="dashboard-grid" style={{ marginBottom: "1.5rem" }}>
        <div className="dashboard-panel" style={{ flex: 1, minWidth: 320 }}>
          <div className="dashboard-section-header">
            <div>
              <h2>Add Expense</h2>
            </div>
          </div>

          {err ? <div className="dashboard-attention" style={{ borderColor: "#fecaca", background: "#fff1f2" }}>{err}</div> : null}

          <form onSubmit={handleSave}>
            <label>Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Expense name" />

            <label>Amount</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
            />

            <label>Description</label>
            <textarea
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional details"
            />

            <label>
              <input
                type="checkbox"
                checked={paid}
                onChange={(e) => setPaid(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              Paid?
            </label>

            <button className="btnPrimary" type="submit" disabled={saving}>
              {saving ? "Saving…" : "+ Add Expense"}
            </button>
          </form>
        </div>

        <div className="dashboard-panel" style={{ minWidth: 320 }}>
          <div className="dashboard-section-header">
            <h2>Summary</h2>
          </div>
          <div style={{ display: "grid", gap: "0.75rem" }}>
            <div className="dashboard-card" style={{ margin: 0 }}>
              <h3>Unpaid Expenses</h3>
              <p>{unpaid.length}</p>
            </div>
            <div className="dashboard-card" style={{ margin: 0 }}>
              <h3>Total Unpaid</h3>
              <p>{new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(totalUnpaid)}</p>
            </div>
            <div className="dashboard-card" style={{ margin: 0 }}>
              <h3>Total Expenses</h3>
              <p>{rows.length}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-panel">
        <div className="dashboard-section-header">
          <h2>Expense Records</h2>
          <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search expenses…"
              style={{ width: 240, padding: "0.5rem", border: "1px solid #d1d5db", borderRadius: 8 }}
            />
            <button className="btn btnGhost" type="button" onClick={load} disabled={loading}>
              Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <p>Loading expenses…</p>
        ) : filtered.length === 0 ? (
          <p>No expenses found.</p>
        ) : (
          <div className="dashboard-table-wrap">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th className="right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((expense) => (
                  <tr key={expense.id}>
                    <td>{fmtDate(expense.createdAt)}</td>
                    <td>{expense.name || "—"}</td>
                    <td>{expense.description || "—"}</td>
                    <td>{new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(safeNum(expense.amount))}</td>
                    <td>{expense.paid ? "Paid" : "Unpaid"}</td>
                    <td className="right">
                      {expense.paid ? (
                        <span style={{ opacity: 0.75 }}>Paid</span>
                      ) : (
                        <button
                          className="btn btnTiny"
                          type="button"
                          onClick={() => markPaid(expense)}
                        >
                          Mark Paid
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
